import React from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import DrawingCanvas, { DrawingCanvasProps } from '../DrawingCanvas/DrawingCanvas';

/**
 * SignaturePad — thin, signature-flavored wrapper around the generalized
 * `DrawingCanvas` atom. All props/behavior are unchanged from before this
 * component was generalized; the signature-specific copy ("Signature
 * captured", "Draw your signature above with mouse or finger", etc.) is now
 * passed through to `DrawingCanvas` as explicit defaults rather than being
 * hardcoded inline, so its two existing call sites
 * (`SubscriptionAgreementModal`, `SuperAdminAgreementModal`) see zero
 * behavior change.
 */
export type SignaturePadProps = Omit<
  DrawingCanvasProps,
  'capturedLabel' | 'notCapturedLabel' | 'helperTextDisabled' | 'helperTextEnabled'
>;

const SignaturePad = React.forwardRef<SignatureCanvas, SignaturePadProps>((props, ref) => {
  // useTranslation (not the bare tr() helper) so the copy re-renders on a
  // language switch instead of freezing at the language it first mounted in.
  const { t } = useTranslation();

  return (
    <DrawingCanvas
      ref={ref}
      ariaLabel={t('g.draw_your_signature', 'Draw your signature')}
      {...props}
      capturedLabel={t('g.signature_captured', 'Signature captured')}
      notCapturedLabel={t('common.not_signed', 'Not signed')}
      helperTextDisabled={t(
        'g.sign_after_you_read_the_agreement',
        'Sign after you read the agreement',
      )}
      helperTextEnabled={t(
        'g.draw_your_signature_above_with_mouse_or_finger',
        'Draw your signature above with mouse or finger',
      )}
    />
  );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
